import React, { useState, useEffect, useRef } from "react";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Circle as CircleIcon,
  Polyline as PolylineIcon,
  MyLocation as MyLocationIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utlis/axiosInstance";
import { useUser } from "../provider/UserProvider";
import GeofenceConnectionsForm from "../components/GeofenceConnectionsForm";
import styles from "./Geofencing.module.css";

// Google Maps type declarations
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

interface Geofence {
  id: string;
  name: string;
  description: string;
  type: "circle" | "polygon" | "linestring";
  center?: {
    latitude: number;
    longitude: number;
  };
  radius?: number;
  coordinates?: Array<{
    latitude: number;
    longitude: number;
  }>;
  area: string;
}

interface FormState {
  name: string;
  description: string;
  area: string;
  originalArea?: string;
  placeName: string;
  coordinates: string;
}

const Geofencing: React.FC = () => {
  const navigate = useNavigate();
  const { permissions } = useUser();

  // Permission checks
  const canViewGeofencing = permissions?.includes("GEOFENCING_LIST");
  const canAddGeofencing = permissions?.includes("GEOFENCING_ADD");
  const canUpdateGeofencing = permissions?.includes("GEOFENCING_UPDATE");
  const canDeleteGeofencing = permissions?.includes("GEOFENCING_DELETE");

  // Map and drawing state
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<any>(null);
  const drawingManagerRef = useRef<any>(null);
  const currentShapeRef = useRef<any>(null);
  const existingGeofencesRef = useRef<any[]>([]);
  const [isMapInitialized, setIsMapInitialized] = useState(false);

  // Data state
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [mode, setMode] = useState<"list" | "add">("list");
  const [editMode, setEditMode] = useState(false);
  const [selectedGeofence, setSelectedGeofence] = useState<Geofence | null>(
    null,
  );
  const [drawingType, setDrawingType] = useState<"circle" | "polygon">(
    "circle",
  );
  const [radius, setRadius] = useState("300");

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [totalCount, setTotalCount] = useState(0);

  // Form state
  const [formData, setFormData] = useState<FormState>({
    name: "",
    description: "",
    area: "",
    placeName: "",
    coordinates: "",
  });

  // Map center and zoom - Updated to show India by default
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 }); // Center of India
  const [mapZoom, setMapZoom] = useState(5); // Zoomed out to show most of India

  // Connection modal state
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [selectedGeofenceId, setSelectedGeofenceId] = useState<string | null>(
    null,
  );

  // Initialize component
  useEffect(() => {
    fetchAllGeofences();
  }, []);

  // Fetch all geofences for client-side filtering
  const fetchAllGeofences = async () => {
    try {
      setLoading(true);
      // Fetch a large number to get all geofences
      const response = await axiosInstance.get("/gpstracker/geofencehandler/", {
        params: {
          limit: 100000,
          offset: 0,
        },
      });
      console.log("All geofences response:", response.data);

      if (response.data.status && response.data.responseData) {
        setGeofences(response.data.responseData);
        setTotalCount(response.data.responseData.length);
      } else {
        setError("Failed to fetch geofences");
        setGeofences([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching all geofences:", error);
      setError("Failed to fetch geofences");
      setGeofences([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, typeFilter]);

  // Initialize map when switching to add/edit mode
  useEffect(() => {
    if (mode === "add") {
      console.log("Mode changed to add, initializing map...");
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (mapRef.current) {
          console.log("Map container is ready, loading Google Maps API...");
          loadGoogleMapsAPI();
        } else {
          console.error("Map container not found!");
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [mode]);

  // Additional check to ensure map initializes
  useEffect(() => {
    if (
      mode === "add" &&
      mapRef.current &&
      !isMapInitialized &&
      !(window.google && window.google.maps)
    ) {
      console.log("Retrying map initialization...");
      const retryTimer = setTimeout(() => {
        loadGoogleMapsAPI();
      }, 500);
      return () => clearTimeout(retryTimer);
    }
  }, [mode, isMapInitialized]);

  // Render editing geofence when map becomes ready
  useEffect(() => {
    if (isMapInitialized && editMode && selectedGeofence && mode === "add") {
      console.log("Map ready, rendering editing geofence...");
      setTimeout(() => {
        renderEditingGeofence(selectedGeofence);
      }, 100);
    }
  }, [isMapInitialized, editMode, selectedGeofence, mode]);

  // Fetch geofences from API
  const fetchGeofences = async (
    currentPage: number = page,
    currentRowsPerPage: number = rowsPerPage,
  ) => {
    try {
      setLoading(true);
      const offset = currentPage * currentRowsPerPage;
      const limit = currentRowsPerPage;

      const response = await axiosInstance.get("/gpstracker/geofencehandler/", {
        params: {
          limit: limit,
          offset: offset,
        },
      });
      console.log("Geofences response:", response.data);

      if (response.data.status && response.data.responseData) {
        setGeofences(response.data.responseData);
        // If the API returns total count, use it; otherwise estimate
        setTotalCount(
          response.data.totalCount ||
          response.data.responseData.length +
          offset +
          (response.data.responseData.length === limit ? limit : 0),
        );
      } else {
        setError("Failed to fetch geofences");
        setGeofences([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching geofences:", error);
      setError("Failed to fetch geofences");
      setGeofences([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch calendars from API
  const fetchCalendars = async () => {
    try {
      const response = await axiosInstance.get("/gpstracker/calendarhandler/");
      console.log("Calendars response:", response.data);

      if (response.data.status && response.data.responseData) {
        // setCalendars(response.data.responseData); // This state is no longer used
      } else {
        // setCalendars([]); // This state is no longer used
      }
    } catch (error) {
      console.error("Error fetching calendars:", error);
      // setCalendars([]); // This state is no longer used
    }
  };

  // Load Google Maps API
  const loadGoogleMapsAPI = () => {
    console.log("Loading Google Maps API...", {
      apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? "Present" : "Missing",
      googleExists: !!window.google,
      mapRefExists: !!mapRef.current,
      mode: mode,
    });

    if (window.google && window.google.maps) {
      console.log("Google Maps API already loaded, initializing map...");
      initializeMap();
      return;
    }

    if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      console.error(
        "Google Maps API key is missing from environment variables!",
      );
      setError("Google Maps API key is not configured");
      return;
    }

    // Check if script already exists to avoid duplicates
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]',
    );
    if (existingScript) {
      console.log(
        "Google Maps script already exists in DOM, waiting for it to load...",
      );
      // Poll until window.google.maps is available
      const pollInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(pollInterval);
          console.log("Google Maps API became available, initializing map...");
          initializeMap();
        }
      }, 100);
      // Stop polling after 10 seconds
      setTimeout(() => clearInterval(pollInterval), 10000);
      return;
    }

    console.log("Creating new Google Maps script element...");
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    console.log(
      `Using API Key: ${apiKey ? apiKey.substring(0, 5) + "..." : "undefined"}`,
    );

    // Use the callback pattern required by the Maps JS API for async loading
    window.initMap = () => {
      console.log("Google Maps API callback fired (window.initMap)");
      initializeMap();
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=drawing,geometry&callback=initMap`;
    script.async = true;
    script.defer = true;
    script.onerror = (e) => {
      console.error("Failed to load Google Maps API script", e);
      setError(
        "Failed to load Google Maps. Please check your internet connection.",
      );
    };
    document.head.appendChild(script);
    console.log("Google Maps script appended to head");
  };

  // Initialize Google Map
  const initializeMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps) {
      console.error("Cannot initialize map:", {
        mapRef: !!mapRef.current,
        google: !!window.google,
        googleMaps: !!(window.google && window.google.maps),
      });
      return;
    }

    // Always reinitialize if we're in add mode and map container exists
    if (isMapInitialized && mode === "add" && googleMapRef.current) {
      console.log("Map already initialized and working");
      return; // Map already initialized and working
    }

    console.log("Initializing Google Map...");

    try {
      const mapOptions = {
        center: mapCenter,
        zoom: mapZoom,
        mapTypeId: "roadmap",
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      };

      googleMapRef.current = new window.google.maps.Map(
        mapRef.current,
        mapOptions,
      );

      console.log("Google Map instance created successfully");
    } catch (error) {
      console.error("Error creating Google Map instance:", error);
      setError("Failed to initialize Google Map");
      return;
    }

    // Initialize drawing manager
    drawingManagerRef.current = new window.google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: false,
      circleOptions: {
        fillColor: "#1976d2",
        fillOpacity: 0.2,
        strokeWeight: 2,
        strokeColor: "#1976d2",
        clickable: false,
        editable: true,
        zIndex: 1,
      },
      polygonOptions: {
        fillColor: "#1976d2",
        fillOpacity: 0.2,
        strokeWeight: 2,
        strokeColor: "#1976d2",
        clickable: false,
        editable: true,
        zIndex: 1,
      },
    });

    drawingManagerRef.current.setMap(googleMapRef.current);

    window.google.maps.event.addListener(
      drawingManagerRef.current,
      "overlaycomplete",
      (event: any) => {
        if (currentShapeRef.current) {
          currentShapeRef.current.setMap(null);
        }

        currentShapeRef.current = event.overlay;
        drawingManagerRef.current.setDrawingMode(null);

        if (event.type === "circle") {
          event.overlay.setOptions({
            fillColor: "#ff9800",
            fillOpacity: 0.3,
            strokeColor: "#ff9800",
            strokeWeight: 2,
            editable: true,
            draggable: true,
          });

          const center = event.overlay.getCenter();
          const radius = event.overlay.getRadius();
          const areaValue = `CIRCLE (${center.lat()} ${center.lng()}, ${radius})`;
          setFormData((prev) => ({ ...prev, area: areaValue }));
          setRadius(radius.toString());

          window.google.maps.event.addListener(
            event.overlay,
            "center_changed",
            () => {
              const center = event.overlay.getCenter();
              const radius = event.overlay.getRadius();
              const areaValue = `CIRCLE (${center.lat()} ${center.lng()}, ${radius})`;
              setFormData((prev) => ({ ...prev, area: areaValue }));
            },
          );

          window.google.maps.event.addListener(
            event.overlay,
            "radius_changed",
            () => {
              const center = event.overlay.getCenter();
              const radius = event.overlay.getRadius();
              const areaValue = `CIRCLE (${center.lat()} ${center.lng()}, ${radius})`;
              setFormData((prev) => ({ ...prev, area: areaValue }));
              setRadius(radius.toString());
            },
          );
        } else if (event.type === "polygon") {
          event.overlay.setOptions({
            fillColor: "#ff9800",
            fillOpacity: 0.3,
            strokeColor: "#ff9800",
            strokeWeight: 2,
            editable: true,
            draggable: true,
          });

          const path = event.overlay.getPath();
          const coordinates: string[] = [];
          path.forEach((latLng: any) => {
            coordinates.push(`${latLng.lat()} ${latLng.lng()}`);
          });
          const areaValue = `POLYGON ((${coordinates.join(", ")}))`;
          setFormData((prev) => ({ ...prev, area: areaValue }));

          // Add listeners for real-time updates
          const updatePolygonArea = () => {
            const path = event.overlay.getPath();
            const coordinates: string[] = [];
            path.forEach((latLng: any) => {
              coordinates.push(`${latLng.lat()} ${latLng.lng()}`);
            });
            console.log("Creating polygon - total points:", coordinates.length);
            console.log("Creating polygon - coordinates:", coordinates);
            const areaValue = `POLYGON ((${coordinates.join(", ")}))`;
            setFormData((prev) => ({ ...prev, area: areaValue }));
          };

          window.google.maps.event.addListener(
            path,
            "set_at",
            updatePolygonArea,
          );
          window.google.maps.event.addListener(
            path,
            "insert_at",
            updatePolygonArea,
          );
          window.google.maps.event.addListener(
            path,
            "remove_at",
            updatePolygonArea,
          );
        }
      },
    );

    // Render existing geofences
    renderGeofencesOnMap();
    setIsMapInitialized(true);
    console.log("Google Map initialized successfully");
  };

  // Clear all existing geofences from map
  const clearExistingGeofences = () => {
    existingGeofencesRef.current.forEach((shape) => {
      if (shape && shape.setMap) {
        shape.setMap(null);
      }
    });
    existingGeofencesRef.current = [];
  };

  // Render geofences on map (only in list mode)
  const renderGeofencesOnMap = () => {
    if (!googleMapRef.current || !window.google || mode !== "list") return;

    // Clear existing geofences first
    clearExistingGeofences();

    geofences.forEach((geofence) => {
      try {
        let shape = null;

        // Parse circle format: "CIRCLE (lat lng, radius)"
        const circleMatch = geofence.area.match(
          /CIRCLE \(([^,]+) ([^,]+), ([^)]+)\)/,
        );
        if (circleMatch) {
          const lat = parseFloat(circleMatch[1]);
          const lng = parseFloat(circleMatch[2]);
          const radius = parseFloat(circleMatch[3]);

          shape = new window.google.maps.Circle({
            center: { lat, lng },
            radius: radius,
            map: googleMapRef.current,
            fillColor: "#1976d2",
            fillOpacity: 0.15,
            strokeColor: "#1976d2",
            strokeWeight: 2,
            clickable: false,
          });
        } else {
          // Parse polygon format - handle double parentheses: POLYGON ((coordinates))
          const polygonMatch = geofence.area.match(/POLYGON \(\((.*)\)\)/);
          if (polygonMatch) {
            const coordinates = polygonMatch[1].split(",").map((coord) => {
              const [lat, lng] = coord.trim().split(" ");
              return { lat: parseFloat(lat), lng: parseFloat(lng) };
            });

            shape = new window.google.maps.Polygon({
              paths: coordinates,
              map: googleMapRef.current,
              fillColor: "#1976d2",
              fillOpacity: 0.15,
              strokeColor: "#1976d2",
              strokeWeight: 2,
              clickable: false,
            });
          }
        }

        if (shape) {
          existingGeofencesRef.current.push(shape);
        }
      } catch (error) {
        console.error("Error rendering geofence:", geofence.id, error);
      }
    });
  };

  // Clear current editing shape
  const clearCurrentShape = () => {
    if (currentShapeRef.current) {
      currentShapeRef.current.setMap(null);
      currentShapeRef.current = null;
    }
  };

  // Render specific geofence for editing
  const renderEditingGeofence = (geofence: Geofence) => {
    if (!googleMapRef.current || !window.google) return;

    // Clear all existing shapes
    clearExistingGeofences();
    clearCurrentShape();

    try {
      // Parse circle format
      const circleMatch = geofence.area.match(
        /CIRCLE \(([^,]+) ([^,]+), ([^)]+)\)/,
      );
      if (circleMatch) {
        const lat = parseFloat(circleMatch[1]);
        const lng = parseFloat(circleMatch[2]);
        const radius = parseFloat(circleMatch[3]);

        // Validate circle data
        if (isNaN(lat) || isNaN(lng) || isNaN(radius)) {
          setError(
            `Invalid circle data: Invalid coordinates or radius (${lat}, ${lng}, ${radius})`,
          );
          console.error("Invalid circle data:", { lat, lng, radius });
          return;
        }

        // Validate coordinate ranges
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          setError(
            `Invalid circle coordinates: Latitude must be between -90 and 90, longitude between -180 and 180`,
          );
          console.error("Circle coordinates out of range:", { lat, lng });
          return;
        }

        // Validate radius
        if (radius <= 0) {
          setError(
            `Invalid circle radius: Radius must be greater than 0 (current: ${radius})`,
          );
          console.error("Invalid circle radius:", radius);
          return;
        }

        setDrawingType("circle");
        setRadius(radius.toString());

        // Create editable circle for the geofence being edited
        currentShapeRef.current = new window.google.maps.Circle({
          center: { lat, lng },
          radius: radius,
          map: googleMapRef.current,
          fillColor: "#ff9800", // Orange color for editing
          fillOpacity: 0.3,
          strokeColor: "#ff9800",
          strokeWeight: 2,
          editable: true,
          draggable: true,
        });

        // Update form when circle is edited
        const updateCircleData = () => {
          const center = currentShapeRef.current.getCenter();
          const radius = currentShapeRef.current.getRadius();
          const areaValue = `CIRCLE (${center.lat()} ${center.lng()}, ${radius})`;
          setFormData((prev) => ({ ...prev, area: areaValue }));
          setRadius(radius.toString());
        };

        // Add event listeners for circle editing
        window.google.maps.event.addListener(
          currentShapeRef.current,
          "center_changed",
          updateCircleData,
        );
        window.google.maps.event.addListener(
          currentShapeRef.current,
          "radius_changed",
          updateCircleData,
        );

        // Center map on circle
        googleMapRef.current.setCenter({ lat, lng });
        googleMapRef.current.setZoom(14);
        return;
      }

      // Parse polygon format - handle double parentheses: POLYGON ((coordinates))
      const polygonMatch = geofence.area.match(/POLYGON \(\((.*)\)\)/);
      if (polygonMatch) {
        const coordinateString = polygonMatch[1];
        console.log("Parsing polygon coordinate string:", coordinateString);

        const coordinates = coordinateString
          .split(",")
          .map((coord) => {
            const trimmedCoord = coord.trim();
            const parts = trimmedCoord.split(/\s+/); // Split by any whitespace

            if (parts.length !== 2) {
              console.error("Invalid coordinate format:", trimmedCoord);
              return null;
            }

            const [lat, lng] = parts;
            const parsedLat = parseFloat(lat);
            const parsedLng = parseFloat(lng);

            // Validate coordinates
            if (isNaN(parsedLat) || isNaN(parsedLng)) {
              console.error("Invalid coordinates found:", lat, lng);
              return null;
            }

            // Validate coordinate ranges
            if (
              parsedLat < -90 ||
              parsedLat > 90 ||
              parsedLng < -180 ||
              parsedLng > 180
            ) {
              console.error(
                "Coordinates out of valid range:",
                parsedLat,
                parsedLng,
              );
              return null;
            }

            return { lat: parsedLat, lng: parsedLng };
          })
          .filter((coord) => coord !== null); // Remove invalid coordinates

        // Check if we have valid coordinates
        if (coordinates.length < 3) {
          setError(
            `Invalid polygon data: Only ${coordinates.length} valid coordinates found. A polygon requires at least 3 coordinates.`,
          );
          console.error(
            "Not enough valid coordinates for polygon:",
            coordinates.length,
          );
          return;
        }

        setDrawingType("polygon");

        // Create editable polygon for the geofence being edited
        currentShapeRef.current = new window.google.maps.Polygon({
          paths: coordinates,
          map: googleMapRef.current,
          fillColor: "#ff9800", // Orange color for editing
          fillOpacity: 0.3,
          strokeColor: "#ff9800",
          strokeWeight: 2,
          editable: true,
          draggable: true,
        });

        // Update form when polygon is edited
        const updatePolygonData = () => {
          const path = currentShapeRef.current.getPath();
          const coordinates: string[] = [];
          path.forEach((latLng: any) => {
            coordinates.push(`${latLng.lat()} ${latLng.lng()}`);
          });
          console.log("Updating polygon - total points:", coordinates.length);
          console.log("Updating polygon - coordinates:", coordinates);
          const areaValue = `POLYGON ((${coordinates.join(", ")}))`;
          setFormData((prev) => ({ ...prev, area: areaValue }));
        };

        // Add event listeners for polygon editing
        const path = currentShapeRef.current.getPath();
        window.google.maps.event.addListener(path, "set_at", updatePolygonData);
        window.google.maps.event.addListener(
          path,
          "insert_at",
          updatePolygonData,
        );
        window.google.maps.event.addListener(
          path,
          "remove_at",
          updatePolygonData,
        );

        // Center map on polygon with proper bounds checking
        const bounds = new window.google.maps.LatLngBounds();
        coordinates.forEach((coord) => {
          if (coord && !isNaN(coord.lat) && !isNaN(coord.lng)) {
            bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
          }
        });

        // Only fit bounds if we have valid bounds
        if (!bounds.isEmpty()) {
          googleMapRef.current.fitBounds(bounds);
        } else {
          setError(
            "Unable to center map on polygon: No valid coordinates found",
          );
          console.error("No valid bounds to fit for polygon");
        }
      } else {
        // No valid shape format found
        setError(
          `Unrecognized geofence format: ${geofence.area}. Expected CIRCLE or POLYGON format.`,
        );
        console.error("Unrecognized geofence area format:", geofence.area);
      }
    } catch (error) {
      console.error("Error rendering editing geofence:", error);
      setError(
        `Error loading geofence data: ${error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  };

  // Handle add geofence
  const handleAddGeofence = () => {
    if (!canAddGeofencing) {
      setError("You don't have permission to add geofences");
      return;
    }
    setMode("add");
    setEditMode(false);
    setSelectedGeofence(null);
    setFormData({
      name: "",
      description: "",
      area: "",
      placeName: "",
      coordinates: "",
    });
    setDrawingType("circle");
    setRadius("300");
    setIsMapInitialized(false); // Reset map initialization

    // Reset map center to India
    setMapCenter({ lat: 20.5937, lng: 78.9629 });
    setMapZoom(5);

    // Clear all shapes when starting fresh
    clearExistingGeofences();
    clearCurrentShape();
  };

  // Handle edit geofence
  const handleEditGeofence = (geofence: Geofence) => {
    if (!canUpdateGeofencing) {
      setError("You don't have permission to edit geofences");
      return;
    }
    setSelectedGeofence(geofence);
    setEditMode(true);
    setMode("add");
    setIsMapInitialized(false); // Reset map initialization

    setFormData({
      name: geofence.name,
      description: geofence.description,
      area: geofence.area, // Keep original area until save
      placeName: "",
      coordinates: "",
    });

    // Store the original area for potential cancellation
    setFormData((prev) => ({ ...prev, originalArea: geofence.area }));

    // Render the specific geofence for editing after map is ready
    setTimeout(() => {
      if (googleMapRef.current && isMapInitialized) {
        renderEditingGeofence(geofence);
      }
    }, 300);
  };

  // Handle delete geofence
  const handleDeleteGeofence = async (id: string) => {
    if (!canDeleteGeofencing) {
      setError("You don't have permission to delete geofences");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this geofence?"))
      return;

    try {
      await axiosInstance.delete(`/gpstracker/geofencehandler/${id}`);
      fetchAllGeofences(); // Refresh the list
    } catch (error) {
      console.error("Error deleting geofence:", error);
      setError("Failed to delete geofence");
    }
  };

  // Validate geofence area data
  const validateGeofenceArea = (
    area: string,
  ): { isValid: boolean; error?: string } => {
    if (!area || area.trim() === "") {
      return { isValid: false, error: "Please draw a geofence on the map" };
    }

    // Validate circle format
    const circleMatch = area.match(/CIRCLE \(([^,]+) ([^,]+), ([^)]+)\)/);
    if (circleMatch) {
      const lat = parseFloat(circleMatch[1]);
      const lng = parseFloat(circleMatch[2]);
      const radius = parseFloat(circleMatch[3]);

      if (isNaN(lat) || isNaN(lng) || isNaN(radius)) {
        return {
          isValid: false,
          error: `Invalid circle data: Invalid coordinates or radius`,
        };
      }

      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return {
          isValid: false,
          error: `Invalid circle coordinates: Latitude must be between -90 and 90, longitude between -180 and 180`,
        };
      }

      if (radius <= 0) {
        return {
          isValid: false,
          error: `Invalid circle radius: Radius must be greater than 0`,
        };
      }

      return { isValid: true };
    }

    // Validate polygon format
    const polygonMatch = area.match(/POLYGON \(\((.*)\)\)/);
    if (polygonMatch) {
      const coordinateString = polygonMatch[1];
      const coordinates = coordinateString
        .split(",")
        .map((coord) => {
          const trimmedCoord = coord.trim();
          const parts = trimmedCoord.split(/\s+/);

          if (parts.length !== 2) {
            return null;
          }

          const [lat, lng] = parts;
          const parsedLat = parseFloat(lat);
          const parsedLng = parseFloat(lng);

          if (isNaN(parsedLat) || isNaN(parsedLng)) {
            return null;
          }

          if (
            parsedLat < -90 ||
            parsedLat > 90 ||
            parsedLng < -180 ||
            parsedLng > 180
          ) {
            return null;
          }

          return { lat: parsedLat, lng: parsedLng };
        })
        .filter((coord) => coord !== null);

      if (coordinates.length < 3) {
        return {
          isValid: false,
          error: `Invalid polygon: Only ${coordinates.length} valid coordinates found. A polygon requires at least 3 coordinates.`,
        };
      }

      return { isValid: true };
    }

    return {
      isValid: false,
      error: `Unrecognized geofence format. Expected CIRCLE or POLYGON format.`,
    };
  };

  // Handle save geofence
  const handleSaveGeofence = async () => {
    try {
      if (!formData.name.trim()) {
        setError("Please enter a name for the geofence");
        return;
      }

      if (!formData.description.trim()) {
        setError("Please enter a description for the geofence");
        return;
      }

      // Validate geofence area data
      const validation = validateGeofenceArea(formData.area);
      if (!validation.isValid) {
        setError(validation.error || "Invalid geofence data");
        return;
      }

      const requestBody = {
        name: formData.name,
        description: formData.description,
        area: formData.area,
      };

      console.log("Final payload for API:", requestBody);

      if (editMode && selectedGeofence) {
        await axiosInstance.put(
          `/gpstracker/geofencehandler/${selectedGeofence.id}`,
          requestBody,
        );
      } else {
        await axiosInstance.post("/gpstracker/geofencehandler/", requestBody);
      }

      // Reset form and return to list
      setMode("list");
      setSelectedGeofence(null);
      setEditMode(false);
      setFormData({
        name: "",
        description: "",
        area: "",
        placeName: "",
        coordinates: "",
      });

      clearCurrentShape();
      clearExistingGeofences();

      fetchAllGeofences(); // Refresh the list
      setError(null);
    } catch (error: any) {
      console.error("Error saving geofence:", error);
      setError(error?.response?.data?.message || "Failed to save geofence");
    }
  };

  const handleCancelEdit = () => {
    if (editMode && selectedGeofence && formData.originalArea) {
      setFormData((prev) => ({ ...prev, area: formData.originalArea! }));
    }

    clearCurrentShape();
    clearExistingGeofences();

    setMode("list");
    setSelectedGeofence(null);
    setEditMode(false);
    setFormData({
      name: "",
      description: "",
      area: "",
      placeName: "",
      coordinates: "",
    });
  };

  // Handle drawing mode change
  const handleDrawingModeChange = (type: "circle" | "polygon") => {
    setDrawingType(type);

    if (!drawingManagerRef.current) return;

    clearCurrentShape();

    if (type === "circle") {
      drawingManagerRef.current.setDrawingMode(
        window.google.maps.drawing.OverlayType.CIRCLE,
      );
    } else {
      drawingManagerRef.current.setDrawingMode(
        window.google.maps.drawing.OverlayType.POLYGON,
      );
    }
  };

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof FormState] as any),
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle select changes
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Filter geofences based on search term and type filter
  const filteredGeofences = geofences.filter((geofence) => {
    // Search filter
    const matchesSearch =
      searchTerm === "" ||
      geofence.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      geofence.description.toLowerCase().includes(searchTerm.toLowerCase());

    // Type filter
    const matchesType =
      typeFilter === "all" ||
      (typeFilter === "circle" && geofence.area.startsWith("CIRCLE")) ||
      (typeFilter === "polygon" && geofence.area.startsWith("POLYGON"));

    return matchesSearch && matchesType;
  });

  // Apply client-side pagination to filtered results
  const paginatedGeofences = filteredGeofences.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Get type icon
  const getTypeIcon = (area: string) => {
    if (area.startsWith("CIRCLE")) {
      return <CircleIcon style={{ color: "#1976d2" }} />;
    } else {
      return <PolylineIcon style={{ color: "#1976d2" }} />;
    }
  };

  // Get type label
  const getTypeLabel = (area: string) => {
    if (area.startsWith("CIRCLE")) return "Circle";
    if (area.startsWith("POLYGON")) return "Polygon";
    if (area.startsWith("LINESTRING")) return "Linestring";
    return "Unknown";
  };

  // Handle place name search using Google Geocoding API
  const handlePlaceSearch = async (placeName: string) => {
    if (!placeName.trim() || !window.google || !googleMapRef.current) return;

    try {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: placeName }, (results: any, status: any) => {
        if (status === "OK" && results && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          // Center map on the found location
          googleMapRef.current.setCenter({ lat, lng });
          googleMapRef.current.setZoom(15);

          // Update coordinates field
          setFormData((prev) => ({
            ...prev,
            coordinates: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          }));
        } else {
          setError(`Could not find location: ${placeName}`);
        }
      });
    } catch (error) {
      console.error("Error searching for place:", error);
      setError("Error searching for location");
    }
  };

  // Handle coordinate navigation
  const handleCoordinateNavigation = (coordinates: string) => {
    if (!coordinates.trim() || !googleMapRef.current) return;

    try {
      // Parse coordinates (expecting format: "lat, lng" or "lat,lng")
      const coords = coordinates.split(",").map((coord) => coord.trim());

      if (coords.length !== 2) {
        setError("Please enter coordinates in format: latitude, longitude");
        return;
      }

      const lat = parseFloat(coords[0]);
      const lng = parseFloat(coords[1]);

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng)) {
        setError("Please enter valid numeric coordinates");
        return;
      }

      if (lat < -90 || lat > 90) {
        setError("Latitude must be between -90 and 90");
        return;
      }

      if (lng < -180 || lng > 180) {
        setError("Longitude must be between -180 and 180");
        return;
      }

      // Center map on the coordinates
      googleMapRef.current.setCenter({ lat, lng });
      googleMapRef.current.setZoom(15);

      // Try to get place name for the coordinates (reverse geocoding)
      if (window.google.maps.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: { lat, lng } },
          (results: any, status: any) => {
            if (status === "OK" && results && results[0]) {
              const placeName = results[0].formatted_address;
              setFormData((prev) => ({ ...prev, placeName }));
            }
          },
        );
      }
    } catch (error) {
      console.error("Error navigating to coordinates:", error);
      setError("Error processing coordinates");
    }
  };

  // Handle place name input change
  const handlePlaceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const placeName = e.target.value;
    setFormData((prev) => ({ ...prev, placeName }));
  };

  // Handle coordinates input change
  const handleCoordinatesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const coordinates = e.target.value;
    setFormData((prev) => ({ ...prev, coordinates }));
  };

  // If user doesn't have view permission, show error message
  if (!canViewGeofencing) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.headerLeft}>
                <div>
                  <div className={styles.headerTitle}>Access Denied</div>
                  <div className={styles.headerSubtitle}>
                    You don't have permission to view geofencing.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {error && (
        <div className={styles.alert}>
          <WarningIcon sx={{ fontSize: 18 }} />
          {error}
        </div>
      )}

      {mode === "list" ? (
        <div className={styles.mainContent}>
          {/* Header Section */}
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.headerLeft}>
                <div>
                  <div className={styles.headerTitle}>
                    Geofencing Management
                  </div>
                  <div className={styles.headerSubtitle}>
                    Create and manage virtual boundaries for your fleet
                  </div>
                </div>
              </div>
              <button
                onClick={handleAddGeofence}
                disabled={!canAddGeofencing}
                className={styles.createButton}
              >
                <AddIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Add New Geofence
              </button>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className={styles.searchFilterContainer}>
            <div className={styles.searchFilterContent}>
              <div className={styles.searchField}>
                <input
                  type="text"
                  placeholder="Search geofences..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Types</option>
                <option value="circle">Circle</option>
                <option value="polygon">Polygon</option>
              </select>
            </div>
          </div>

          {/* Geofences Table */}
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead className={styles.tableHead}>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {loading ? (
                  <tr>
                    <td
                      colSpan={4}
                      align="center"
                      className={styles.loadingContainer}
                    >
                      <div className={styles.loadingText}>
                        Loading geofences...
                      </div>
                    </td>
                  </tr>
                ) : filteredGeofences.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <div className={styles.emptyState}>
                        <MyLocationIcon className={styles.emptyIcon} />
                        <div className={styles.emptyTitle}>
                          No Geofences Found
                        </div>
                        <div className={styles.emptySubtitle}>
                          {searchTerm || typeFilter !== "all"
                            ? "Try adjusting your search filters"
                            : "Create your first geofence to get started"}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  [...paginatedGeofences].reverse().map((geofence) => (
                    <tr key={geofence.id}>
                      <td>
                        <div className={styles.geofenceInfo}>
                          <div className={styles.geofenceDetails}>
                            <div className={styles.geofenceName}>
                              {geofence.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles.geofenceDescription}>
                          {geofence.description}
                        </div>
                      </td>
                      <td>
                        <span className={styles.typeChip}>
                          {getTypeLabel(geofence.area)}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className={styles.actionButtons}>
                          <button
                            onClick={() => {
                              setSelectedGeofenceId(geofence.id);
                              setConnectionModalOpen(true);
                            }}
                            className={`${styles.actionButton} ${styles.linkButton}`}
                            title="Link"
                          >
                            <LinkIcon sx={{ fontSize: 16 }} />
                          </button>
                          <button
                            onClick={() => handleEditGeofence(geofence)}
                            disabled={!canUpdateGeofencing}
                            className={`${styles.actionButton} ${styles.editButton}`}
                            title="Edit"
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </button>
                          <button
                            onClick={() => handleDeleteGeofence(geofence.id)}
                            disabled={!canDeleteGeofencing}
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            title="Delete"
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                Showing {page * rowsPerPage + 1} to{" "}
                {Math.min((page + 1) * rowsPerPage, filteredGeofences.length)}{" "}
                of {filteredGeofences.length} results
              </div>
              <div className={styles.paginationControls}>
                <button
                  onClick={() => handleChangePage(null, page - 1)}
                  disabled={page === 0}
                  className={styles.paginationButton}
                >
                  Previous
                </button>
                <button
                  onClick={() => handleChangePage(null, page + 1)}
                  disabled={
                    (page + 1) * rowsPerPage >= filteredGeofences.length
                  }
                  className={styles.paginationButton}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Add/Edit Mode */
        <div className={styles.mapContainer}>
          {/* Header */}
          <div className={styles.mapHeader}>
            <button onClick={handleCancelEdit} className={styles.backButton}>
              <ArrowBackIcon />
            </button>
            <div className={styles.mapTitle}>
              {editMode ? "Edit Geofence" : "Create New Geofence"}
            </div>
          </div>

          {/* Map and Form Container */}
          <div className={styles.mapContent}>
            {/* Map Container */}
            <div className={styles.mapArea}>
              <div ref={mapRef} className={styles.mapElement} />

              {/* Map Loading State */}
              {!isMapInitialized && (
                <div className={styles.mapLoading}>
                  <div className={styles.loadingText}>Loading Map...</div>
                  <div className={styles.loadingText}>
                    Please wait while we initialize the map
                  </div>
                </div>
              )}

              {/* Map Controls */}
              <div className={styles.mapControls}>
                <button
                  onClick={() => handleDrawingModeChange("circle")}
                  className={`${styles.mapControlButton} ${drawingType === "circle" ? styles.active : ""}`}
                  title="Draw Circle"
                >
                  <CircleIcon />
                </button>
                <button
                  onClick={() => handleDrawingModeChange("polygon")}
                  className={`${styles.mapControlButton} ${drawingType === "polygon" ? styles.active : ""}`}
                  title="Draw Polygon"
                >
                  <PolylineIcon />
                </button>
              </div>
            </div>

            {/* Form Panel */}
            <div className={styles.formPanel}>
              <div className={styles.formContent}>
                <div className={styles.formTitle}>Geofence Details</div>

                <div className={styles.formField}>
                  <label className={styles.formLabel}>Search Place</label>
                  <input
                    type="text"
                    value={formData.placeName}
                    onChange={handlePlaceNameChange}
                    onKeyPress={(e) => {
                      if (e.key === "Enter")
                        handlePlaceSearch(formData.placeName);
                    }}
                    className={styles.formInput}
                    placeholder="Enter place name"
                  />
                </div>

                <div className={styles.formField}>
                  <label className={styles.formLabel}>Coordinates</label>
                  <input
                    type="text"
                    value={formData.coordinates}
                    onChange={handleCoordinatesChange}
                    onKeyPress={(e) => {
                      if (e.key === "Enter")
                        handleCoordinateNavigation(formData.coordinates);
                    }}
                    className={styles.formInput}
                    placeholder="lat, lng"
                  />
                </div>

                <div className={styles.formField}>
                  <label className={styles.formLabel}>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={styles.formInput}
                    placeholder="Enter geofence name"
                    required
                  />
                </div>

                <div className={styles.formField}>
                  <label className={styles.formLabel}>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={styles.formTextarea}
                    placeholder="Enter geofence description"
                    required
                  />
                </div>

                {drawingType === "circle" && (
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Radius (meters)</label>
                    <input
                      type="number"
                      value={radius}
                      onChange={(e) => {
                        setRadius(e.target.value);
                        if (currentShapeRef.current) {
                          currentShapeRef.current.setRadius(
                            parseFloat(e.target.value),
                          );
                          const center = currentShapeRef.current.getCenter();
                          const areaValue = `CIRCLE (${center.lat()} ${center.lng()}, ${e.target.value})`;
                          setFormData((prev) => ({ ...prev, area: areaValue }));
                        }
                      }}
                      className={styles.formInput}
                      placeholder="Enter radius in meters"
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className={styles.formActions}>
                  <button
                    onClick={() => navigate(-1)}
                    className={styles.cancelButton}
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSaveGeofence}
                    disabled={!formData.name || !formData.description}
                    className={styles.saveButton}
                  >
                    {editMode ? "Update Geofence" : "Create Geofence"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedGeofenceId && (
        <GeofenceConnectionsForm
          id={selectedGeofenceId}
          open={connectionModalOpen}
          onClose={() => {
            setConnectionModalOpen(false);
            setSelectedGeofenceId(null);
          }}
        />
      )}
    </div>
  );
};

export default Geofencing;
